import { AlertService } from '@anecdotes/design-system/alert';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-legal-settings-text-input',
  templateUrl: './legal-settings-text-input.component.html',
  styleUrls: ['./legal-settings-text-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegalSettingsTextInputComponent implements OnInit, OnChanges {
  private initialValue: string;
  isRequired: boolean;

  @Input()
  value: string;

  @Input()
  title: string;

  @Input()
  placeholder: string;

  @Input()
  subtitle?: string;

  @Input()
  saveButtonText: string;

  @Input()
  saveOnBlur?: boolean;

  @Input()
  validators: ValidatorFn[] = [];

  @Input()
  errorText: Record<string, string>;

  @Input()
  submitHandlerFunction: (value: string) => Promise<void>;

  inputText: FormControl<string>;
  submitting$ = new Subject<boolean>();

  get submitDisabled(): boolean {
    const isInvalid = this.inputText.invalid || this.inputText.value === this.initialValue;

    return this.isRequired ? (isInvalid || !this.inputText.value): isInvalid;
  }

  constructor(private alertService: AlertService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('value' in changes && !changes.value.firstChange && this.value !== this.initialValue) {
      this.initialValue = this.value;
      this.inputText.setValue(this.value);
    }
  }

  ngOnInit(): void {
    this.isRequired = this.validators.includes(Validators.required);
    this.initialValue = this.value;

    this.inputText = new FormControl<string>(
      this.value || '',
      this.isRequired
        ? this.validators.filter((validator) => validator !== Validators.required)
        : this.validators,
    );
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  async handleBlur(): Promise<void> {
    if (this.saveOnBlur) {
      await this.save();
    }
  }

  async save(): Promise<void> {
    if (this.submitDisabled) {
      return;
    }

    this.submitting$.next(true);

    try {
      await this.submitHandlerFunction(this.inputText.value.trim());
    } catch {
      this.alertService.openActionErrorAlert({
        customBaseTranslationKey: this.buildTranslationKey('genericFailedSaving'),
      });
    } finally {
      this.submitting$.next(false);
    }
  }
}
